import { blockDef, blockNameOf, blockOf, faceTexture, stateOf } from '../../shared/registry/blocks.ts';
import { Dir } from '../../shared/registry/direction.ts';
import { SPRITE_INDEX, itemById, type ItemDef } from '../../shared/registry/items.ts';
import { TEXTURE_SIZE } from '../../shared/registry/textures.ts';
import type { NetItemStack } from '../../shared/net/protocol.ts';

/**
 * The heads-up display and inventory screens.
 *
 * Drawn with a 2D canvas layered over the WebGL view. That keeps text, tooltips
 * and slot layout straightforward, and the HUD only redraws when something
 * changes or an animated element (the hunger shake, the break overlay) is
 * active, so it costs very little per frame.
 */

export interface HudState {
  health: number;
  maxHealth: number;
  food: number;
  air: number;
  xpLevel: number;
  xpProgress: number;
  gameMode: number;
  hotbar: (NetItemStack | null)[];
  selectedSlot: number;
  dead: boolean;
  /** Held item name to flash above the hotbar. */
  itemNameFlash: { name: string; until: number } | null;
}

export interface WindowState {
  open: boolean;
  id: number;
  kind: string;
  slots: (NetItemStack | null)[];
  carried: NetItemStack | null;
  furnace: { burn: number; cook: number } | null;
}

export interface SlotRect {
  index: number;
  x: number;
  y: number;
  size: number;
}

const SLOT = 44;
const GAP = 4;

export class Hud {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private blockImage: HTMLImageElement | null = null;
  private itemImage: HTMLImageElement | null = null;
  /** Cached rendered item icons, keyed by "item:damage". */
  private readonly iconCache = new Map<string, HTMLCanvasElement>();

  chatLines: { text: string; at: number }[] = [];
  /** Boss bar shown during the dragon fight. */
  boss: { name: string; progress: number; crystals: number } | null = null;
  /** Prompt shown while the mouse is not captured. */
  capturePrompt: string | null = null;
  chatInput: string | null = null;
  debugVisible = false;
  debugLines: string[] = [];
  /** Slot hit-boxes from the last window draw, for mouse routing. */
  slotRects: SlotRect[] = [];
  mouseX = 0;
  mouseY = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas not available');
    this.ctx = ctx;
  }

  async loadAtlases(): Promise<void> {
    const [blocks, items] = await Promise.all([loadImage('blocks.png'), loadImage('items.png')]);
    this.blockImage = blocks;
    this.itemImage = items;
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.floor(this.canvas.clientWidth * dpr);
    const h = Math.floor(this.canvas.clientHeight * dpr);
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  }

  get width(): number {
    return this.canvas.width;
  }

  get height(): number {
    return this.canvas.height;
  }

  private get scale(): number {
    // Keep the HUD a readable size on both a laptop screen and a 4K monitor.
    return Math.max(1, Math.min(3, Math.round(this.canvas.height / 540)));
  }

  addChat(text: string): void {
    this.chatLines.push({ text, at: performance.now() });
    if (this.chatLines.length > 60) this.chatLines.shift();
  }

  // -------------------------------------------------------------------------
  // Item icons
  // -------------------------------------------------------------------------

  /**
   * Render an item to a cached offscreen canvas: flat sprites are blitted from
   * the item atlas, blocks are drawn as a small isometric cube built from three
   * of their own faces.
   */
  private icon(stack: NetItemStack): HTMLCanvasElement | null {
    const def = itemById(stack.item);
    if (!def) return null;
    const key = `${stack.item}`;
    const cached = this.iconCache.get(key);
    if (cached) return cached;

    const size = 64;
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;

    if (def.sprite === 'block_icon' && def.block && this.blockImage) {
      this.drawBlockIcon(g, def.block, size);
    } else if (this.itemImage) {
      const layer = SPRITE_INDEX.get(def.id);
      if (layer !== undefined) {
        g.drawImage(
          this.itemImage,
          0, layer * TEXTURE_SIZE, TEXTURE_SIZE, TEXTURE_SIZE,
          0, 0, size, size,
        );
      }
    }
    this.iconCache.set(key, c);
    return c;
  }

  private drawBlockIcon(g: CanvasRenderingContext2D, blockName: string, size: number): void {
    const img = this.blockImage!;
    let state: number;
    try {
      state = stateOf(blockName);
    } catch {
      return;
    }
    const def = blockOf(state);
    const topLayer = faceTexture(state, Dir.UP);
    const leftLayer = faceTexture(state, Dir.WEST);
    const rightLayer = faceTexture(state, Dir.SOUTH);

    // Plants and other non-cubes read better drawn flat.
    if (def.render !== 'cube' && def.render !== 'box') {
      g.drawImage(img, 0, topLayer * TEXTURE_SIZE, TEXTURE_SIZE, TEXTURE_SIZE, size * 0.08, size * 0.08, size * 0.84, size * 0.84);
      return;
    }

    const cx = size / 2;
    const w = size * 0.44;
    const h = size * 0.25;
    const bodyH = size * 0.34;
    const topY = size * 0.13;

    const face = (
      layer: number,
      transform: [number, number, number, number, number, number],
      shade: number,
    ) => {
      g.save();
      g.beginPath();
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.transform(...transform);
      g.drawImage(img, 0, layer * TEXTURE_SIZE, TEXTURE_SIZE, TEXTURE_SIZE, 0, 0, 1, 1);
      g.restore();
      if (shade < 1) {
        g.save();
        g.globalCompositeOperation = 'source-atop';
        g.globalAlpha = 1 - shade;
        g.fillStyle = '#000';
        g.setTransform(1, 0, 0, 1, 0, 0);
        g.transform(...transform);
        g.fillRect(0, 0, 1, 1);
        g.restore();
      }
    };

    // Top rhombus, then the two visible side faces, as sheared unit squares.
    face(topLayer, [w, h, -w, h, cx, topY], 1.0);
    face(leftLayer, [w, h, 0, bodyH, cx - w, topY + h], 0.72);
    face(rightLayer, [w, -h, 0, bodyH, cx, topY + h * 2], 0.85);
  }

  drawItem(stack: NetItemStack | null, x: number, y: number, size: number): void {
    if (!stack || stack.count <= 0) return;
    const icon = this.icon(stack);
    const g = this.ctx;
    if (icon) {
      g.imageSmoothingEnabled = false;
      g.drawImage(icon, x, y, size, size);
    }
    const def = itemById(stack.item);
    const s = this.scale;
    if (stack.count > 1) {
      g.font = `bold ${7 * s}px "DejaVu Sans Mono", ui-monospace, monospace`;
      g.textAlign = 'right';
      g.textBaseline = 'bottom';
      g.fillStyle = '#000';
      g.fillText(String(stack.count), x + size - 1 * s, y + size + 1 * s);
      g.fillStyle = '#fff';
      g.fillText(String(stack.count), x + size - 2 * s, y + size);
    }
    if (def?.durability && stack.damage > 0) {
      const frac = 1 - stack.damage / def.durability;
      const barW = size * 0.8;
      const barX = x + size * 0.1;
      const barY = y + size * 0.86;
      g.fillStyle = '#000';
      g.fillRect(barX, barY, barW, 4 * s * 0.6);
      g.fillStyle = `hsl(${frac * 110}, 90%, 45%)`;
      g.fillRect(barX, barY, barW * frac, 4 * s * 0.6);
    }
  }

  // -------------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------------

  draw(state: HudState, window: WindowState, inventoryOpen: boolean, inventorySlots: (NetItemStack | null)[]): void {
    const g = this.ctx;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, this.width, this.height);
    this.slotRects = [];

    if (state.gameMode !== 2) this.drawCrosshair();
    if (this.boss) this.drawBossBar();
    if (this.capturePrompt && !inventoryOpen && !window.open && !state.dead) {
      this.drawCapturePrompt();
    }
    this.drawHotbar(state);
    if (state.gameMode === 0) this.drawStatusBars(state);
    this.drawChat();
    if (this.debugVisible) this.drawDebug();
    if (inventoryOpen || window.open) this.drawWindow(state, window, inventorySlots);
    if (state.dead) this.drawDeathScreen();
  }

  private drawCrosshair(): void {
    const g = this.ctx;
    const cx = this.width / 2;
    const cy = this.height / 2;
    const s = this.scale;
    const len = 8 * s;
    const thickness = 2 * s;
    g.save();
    // Difference blending keeps the crosshair visible against any background.
    g.globalCompositeOperation = 'difference';
    g.fillStyle = '#fff';
    g.fillRect(cx - len, cy - thickness / 2, len * 2, thickness);
    g.fillRect(cx - thickness / 2, cy - len, thickness, len * 2);
    g.restore();
  }

  private drawBossBar(): void {
    const boss = this.boss!;
    const g = this.ctx;
    const s = this.scale;
    const width = Math.min(this.width * 0.55, 360 * s);
    const x = (this.width - width) / 2;
    const y = 18 * s;

    g.font = `bold ${10 * s}px system-ui, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'bottom';
    g.fillStyle = '#000';
    g.fillText(boss.name, this.width / 2 + 1 * s, y - 3 * s + 1 * s);
    g.fillStyle = '#e6d5ff';
    g.fillText(boss.name, this.width / 2, y - 3 * s);

    g.fillStyle = 'rgba(0,0,0,0.65)';
    g.fillRect(x, y, width, 8 * s);
    g.fillStyle = '#b44cff';
    g.fillRect(x, y, width * Math.max(0, Math.min(1, boss.progress)), 8 * s);
    g.strokeStyle = 'rgba(255,255,255,0.25)';
    g.lineWidth = 1 * s;
    g.strokeRect(x, y, width, 8 * s);

    if (boss.crystals > 0) {
      g.font = `${8 * s}px system-ui, sans-serif`;
      g.fillStyle = '#f0c0ff';
      g.fillText(`${boss.crystals} end crystal${boss.crystals === 1 ? '' : 's'} still healing it`, this.width / 2, y + 20 * s);
    }
  }

  /** Centre-screen nudge telling the player how to take control of the view. */
  private drawCapturePrompt(): void {
    const g = this.ctx;
    const s = this.scale;
    const text = this.capturePrompt!;
    g.font = `${11 * s}px system-ui, sans-serif`;
    const width = g.measureText(text).width + 24 * s;
    const height = 26 * s;
    const x = (this.width - width) / 2;
    const y = this.height / 2 + 40 * s;

    g.fillStyle = 'rgba(10,12,18,0.72)';
    roundRect(g, x, y, width, height, 5 * s);
    g.fill();
    g.strokeStyle = 'rgba(255,255,255,0.18)';
    g.lineWidth = 1 * s;
    g.stroke();

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = '#dfe6f2';
    g.fillText(text, this.width / 2, y + height / 2);
  }

  private drawHotbar(state: HudState): void {
    const g = this.ctx;
    const s = this.scale;
    const slot = 20 * s;
    const pad = 2 * s;
    const total = 9 * slot + 10 * pad;
    const x0 = (this.width - total) / 2;
    const y0 = this.height - slot - 10 * s - (state.gameMode === 0 ? 22 * s : 0);

    g.fillStyle = 'rgba(0,0,0,0.55)';
    roundRect(g, x0, y0 - pad, total, slot + pad * 2, 4 * s);
    g.fill();

    for (let i = 0; i < 9; i++) {
      const x = x0 + pad + i * (slot + pad);
      const y = y0;
      g.fillStyle = 'rgba(255,255,255,0.08)';
      g.fillRect(x, y, slot, slot);
      this.drawItem(state.hotbar[i] ?? null, x + 2 * s, y + 2 * s, slot - 4 * s);
      if (i === state.selectedSlot) {
        g.strokeStyle = '#fff';
        g.lineWidth = 2 * s;
        g.strokeRect(x - 1 * s, y - 1 * s, slot + 2 * s, slot + 2 * s);
      }
    }

    // Item name flash above the hotbar.
    const flash = state.itemNameFlash;
    if (flash && performance.now() < flash.until) {
      const alpha = Math.min(1, (flash.until - performance.now()) / 500);
      g.font = `${9 * s}px system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'bottom';
      g.fillStyle = `rgba(0,0,0,${alpha * 0.7})`;
      g.fillText(flash.name, this.width / 2 + 1 * s, y0 - 8 * s + 1 * s);
      g.fillStyle = `rgba(255,255,255,${alpha})`;
      g.fillText(flash.name, this.width / 2, y0 - 8 * s);
    }
  }

  private drawStatusBars(state: HudState): void {
    const g = this.ctx;
    const s = this.scale;
    const slot = 20 * s;
    const pad = 2 * s;
    const total = 9 * slot + 10 * pad;
    const x0 = (this.width - total) / 2;
    const y = this.height - slot - 10 * s - 22 * s;

    const iconSize = 9 * s;
    const step = 8 * s;

    // Hearts, left to right.
    const hearts = Math.ceil(state.maxHealth / 2);
    for (let i = 0; i < hearts; i++) {
      const filled = state.health - i * 2;
      drawHeart(g, x0 + i * step, y - 12 * s, iconSize, filled >= 2 ? 1 : filled > 0 ? 0.5 : 0);
    }
    // Hunger, right to left.
    const drumsticks = 10;
    for (let i = 0; i < drumsticks; i++) {
      const filled = state.food - (drumsticks - 1 - i) * 2;
      drawDrumstick(g, x0 + total - iconSize - i * step, y - 12 * s, iconSize, filled >= 2 ? 1 : filled > 0 ? 0.5 : 0);
    }
    // Air bubbles when underwater.
    if (state.air < 10) {
      for (let i = 0; i < state.air; i++) {
        g.fillStyle = '#cfefff';
        g.beginPath();
        g.arc(x0 + total - iconSize / 2 - i * step, y - 22 * s, iconSize * 0.35, 0, Math.PI * 2);
        g.fill();
      }
    }

    // XP bar and level.
    const barY = y - 2 * s;
    g.fillStyle = 'rgba(0,0,0,0.6)';
    g.fillRect(x0, barY, total, 4 * s);
    g.fillStyle = '#7ee02a';
    g.fillRect(x0, barY, total * Math.max(0, Math.min(1, state.xpProgress)), 4 * s);
    if (state.xpLevel > 0) {
      g.font = `bold ${9 * s}px system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'bottom';
      g.fillStyle = '#000';
      g.fillText(String(state.xpLevel), this.width / 2 + 1 * s, barY - 1 * s + 1 * s);
      g.fillStyle = '#7ee02a';
      g.fillText(String(state.xpLevel), this.width / 2, barY - 1 * s);
    }
  }

  private drawChat(): void {
    const g = this.ctx;
    const s = this.scale;
    const now = performance.now();
    const visible = this.chatInput !== null
      ? this.chatLines.slice(-14)
      : this.chatLines.filter((l) => now - l.at < 9000).slice(-10);

    g.font = `${8 * s}px system-ui, sans-serif`;
    g.textAlign = 'left';
    g.textBaseline = 'bottom';
    let y = this.height - 70 * s;
    for (let i = visible.length - 1; i >= 0; i--) {
      const line = visible[i]!;
      const alpha = this.chatInput !== null ? 1 : Math.min(1, (9000 - (now - line.at)) / 1500);
      const width = g.measureText(line.text).width;
      g.fillStyle = `rgba(0,0,0,${0.4 * alpha})`;
      g.fillRect(6 * s, y - 10 * s, width + 8 * s, 12 * s);
      g.fillStyle = `rgba(255,255,255,${alpha})`;
      g.fillText(line.text, 10 * s, y);
      y -= 12 * s;
    }

    if (this.chatInput !== null) {
      const boxY = this.height - 56 * s;
      g.fillStyle = 'rgba(0,0,0,0.65)';
      g.fillRect(6 * s, boxY, this.width - 12 * s, 14 * s);
      g.fillStyle = '#fff';
      const caret = Math.floor(now / 500) % 2 === 0 ? '_' : ' ';
      g.fillText(`> ${this.chatInput}${caret}`, 10 * s, boxY + 11 * s);
    }
  }

  private drawDebug(): void {
    const g = this.ctx;
    const s = this.scale;
    g.font = `${8 * s}px "DejaVu Sans Mono", ui-monospace, monospace`;
    g.textAlign = 'left';
    g.textBaseline = 'top';
    let y = 8 * s;
    for (const line of this.debugLines) {
      const w = g.measureText(line).width;
      g.fillStyle = 'rgba(0,0,0,0.5)';
      g.fillRect(6 * s, y - 1 * s, w + 8 * s, 11 * s);
      g.fillStyle = '#e8e8e8';
      g.fillText(line, 10 * s, y);
      y += 11 * s;
    }
  }

  private drawDeathScreen(): void {
    const g = this.ctx;
    const s = this.scale;
    g.fillStyle = 'rgba(90,0,0,0.45)';
    g.fillRect(0, 0, this.width, this.height);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `bold ${28 * s}px system-ui, sans-serif`;
    g.fillStyle = '#fff';
    g.fillText('You died', this.width / 2, this.height / 2 - 20 * s);
    g.font = `${11 * s}px system-ui, sans-serif`;
    g.fillText('Press R to respawn', this.width / 2, this.height / 2 + 16 * s);
  }

  // -------------------------------------------------------------------------
  // Inventory and container windows
  // -------------------------------------------------------------------------

  /**
   * Draw the open window. Slot rectangles are recorded so mouse events can be
   * mapped back to window slot indices without duplicating the layout logic.
   */
  private drawWindow(state: HudState, window: WindowState, inventorySlots: (NetItemStack | null)[]): void {
    const g = this.ctx;
    const s = this.scale;
    const slot = 26 * s;
    const gap = 3 * s;

    g.fillStyle = 'rgba(0,0,0,0.6)';
    g.fillRect(0, 0, this.width, this.height);

    const kind = window.open ? window.kind : 'inventory';
    const layout = windowLayout(kind);
    const panelW = layout.width * (slot + gap) + 16 * s;
    const panelH = layout.height * (slot + gap) + 40 * s;
    const px = (this.width - panelW) / 2;
    const py = (this.height - panelH) / 2;

    g.fillStyle = 'rgba(28,28,32,0.96)';
    roundRect(g, px, py, panelW, panelH, 6 * s);
    g.fill();
    g.strokeStyle = 'rgba(255,255,255,0.14)';
    g.lineWidth = 1 * s;
    g.stroke();

    g.font = `bold ${10 * s}px system-ui, sans-serif`;
    g.textAlign = 'left';
    g.textBaseline = 'top';
    g.fillStyle = '#dcdcdc';
    g.fillText(layout.title, px + 10 * s, py + 8 * s);

    const originX = px + 8 * s;
    const originY = py + 26 * s;

    const drawSlot = (index: number, col: number, row: number, stack: NetItemStack | null) => {
      const x = originX + col * (slot + gap);
      const y = originY + row * (slot + gap);
      const hovered =
        this.mouseX >= x && this.mouseX < x + slot && this.mouseY >= y && this.mouseY < y + slot;
      g.fillStyle = hovered ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)';
      g.fillRect(x, y, slot, slot);
      this.drawItem(stack, x + 2 * s, y + 2 * s, slot - 4 * s);
      this.slotRects.push({ index, x, y, size: slot });
    };

    const slots = window.open ? window.slots : this.inventoryWindowSlots(inventorySlots);
    for (const placement of layout.slots) {
      const index = placement.index;
      drawSlot(index, placement.col, placement.row, slots[index] ?? null);
    }

    if (kind === 'furnace' && window.furnace) {
      // Burn and progress indicators between the furnace slots.
      const fx = originX + 1.2 * (slot + gap);
      const fy = originY + 0.5 * (slot + gap);
      g.fillStyle = 'rgba(255,255,255,0.15)';
      g.fillRect(fx, fy, 6 * s, slot);
      g.fillStyle = '#ff9a3c';
      const bh = (window.furnace.burn / 100) * slot;
      g.fillRect(fx, fy + slot - bh, 6 * s, bh);
      g.fillStyle = 'rgba(255,255,255,0.15)';
      g.fillRect(fx + 12 * s, fy + slot / 2 - 3 * s, slot, 6 * s);
      g.fillStyle = '#e8e8e8';
      g.fillRect(fx + 12 * s, fy + slot / 2 - 3 * s, (window.furnace.cook / 100) * slot, 6 * s);
    }

    // Carried stack follows the cursor.
    if (window.carried) {
      this.drawItem(window.carried, this.mouseX - slot / 2, this.mouseY - slot / 2, slot);
    } else {
      // Tooltip for the hovered slot.
      const hovered = this.slotRects.find(
        (r) => this.mouseX >= r.x && this.mouseX < r.x + r.size && this.mouseY >= r.y && this.mouseY < r.y + r.size,
      );
      const stack = hovered ? slots[hovered.index] : null;
      if (stack) this.drawTooltip(stack);
    }
  }

  /** Map the player inventory array onto the always-open inventory window. */
  private inventoryWindowSlots(inventory: (NetItemStack | null)[]): (NetItemStack | null)[] {
    // Window slot order: 0 craft output, 1-4 craft grid, 5-40 inventory, 41-44 armour, 45 offhand.
    const out: (NetItemStack | null)[] = new Array(46).fill(null);
    for (let i = 0; i < 36; i++) out[5 + i] = inventory[i] ?? null;
    for (let i = 0; i < 4; i++) out[41 + i] = inventory[36 + i] ?? null;
    out[45] = inventory[40] ?? null;
    return out;
  }

  private drawTooltip(stack: NetItemStack): void {
    const def = itemById(stack.item);
    if (!def) return;
    const g = this.ctx;
    const s = this.scale;
    const name = prettyName(def);
    const lines = [name];
    if (def.tool) lines.push(`${def.tool.kind} • tier ${def.tool.tier} • ${def.tool.damage} dmg`);
    if (def.armor) lines.push(`armour ${def.armor.defense}`);
    if (def.food) lines.push(`food ${def.food.hunger}`);
    if (def.durability) lines.push(`durability ${def.durability - stack.damage}/${def.durability}`);

    g.font = `${8 * s}px system-ui, sans-serif`;
    const w = Math.max(...lines.map((l) => g.measureText(l).width)) + 12 * s;
    const h = lines.length * 11 * s + 8 * s;
    let x = this.mouseX + 14 * s;
    let y = this.mouseY + 8 * s;
    if (x + w > this.width) x = this.width - w - 4 * s;
    if (y + h > this.height) y = this.height - h - 4 * s;

    g.fillStyle = 'rgba(12,12,18,0.95)';
    roundRect(g, x, y, w, h, 3 * s);
    g.fill();
    g.strokeStyle = def.rare ? 'rgba(220,160,255,0.7)' : 'rgba(255,255,255,0.18)';
    g.lineWidth = 1 * s;
    g.stroke();

    g.textAlign = 'left';
    g.textBaseline = 'top';
    lines.forEach((line, i) => {
      g.fillStyle = i === 0 ? (def.rare ? '#e0a8ff' : '#ffffff') : '#a8a8b0';
      g.fillText(line, x + 6 * s, y + 4 * s + i * 11 * s);
    });
  }

  /** Window slot index under the cursor, or -1. */
  slotAt(x: number, y: number): number {
    for (const r of this.slotRects) {
      if (x >= r.x && x < r.x + r.size && y >= r.y && y < r.y + r.size) return r.index;
    }
    return -1;
  }
}

interface SlotPlacement {
  index: number;
  col: number;
  row: number;
}

interface WindowLayout {
  title: string;
  width: number;
  height: number;
  slots: SlotPlacement[];
}

/**
 * Slot placement per window kind. Index order matches the server's `Window`
 * construction, so a click can be forwarded verbatim.
 */
function windowLayout(kind: string): WindowLayout {
  const slots: SlotPlacement[] = [];
  const playerGrid = (startIndex: number, startRow: number) => {
    // 27 main slots then 9 hotbar, matching the server's slot ordering.
    for (let i = 0; i < 27; i++) {
      slots.push({ index: startIndex + 9 + i, col: i % 9, row: startRow + Math.floor(i / 9) });
    }
    for (let i = 0; i < 9; i++) {
      slots.push({ index: startIndex + i, col: i, row: startRow + 3.4 });
    }
  };

  switch (kind) {
    case 'chest': {
      for (let i = 0; i < 27; i++) slots.push({ index: i, col: i % 9, row: Math.floor(i / 9) });
      playerGrid(27, 3.6);
      return { title: 'Chest', width: 9, height: 8, slots };
    }
    case 'dispenser': {
      for (let i = 0; i < 9; i++) slots.push({ index: i, col: 3 + (i % 3), row: Math.floor(i / 3) });
      playerGrid(9, 3.6);
      return { title: 'Dispenser', width: 9, height: 8, slots };
    }
    case 'hopper': {
      for (let i = 0; i < 5; i++) slots.push({ index: i, col: 2 + i, row: 0 });
      playerGrid(5, 1.6);
      return { title: 'Hopper', width: 9, height: 6, slots };
    }
    case 'furnace': {
      slots.push({ index: 0, col: 0, row: 0 });
      slots.push({ index: 1, col: 0, row: 2 });
      slots.push({ index: 2, col: 4, row: 1 });
      playerGrid(3, 3.6);
      return { title: 'Furnace', width: 9, height: 8, slots };
    }
    case 'crafting_table': {
      slots.push({ index: 0, col: 6, row: 1 });
      for (let i = 0; i < 9; i++) slots.push({ index: 1 + i, col: i % 3, row: Math.floor(i / 3) });
      playerGrid(10, 3.6);
      return { title: 'Crafting', width: 9, height: 8, slots };
    }
    default: {
      // Player inventory: armour column on the left, 2x2 crafting on the
      // right, then the main grid and hotbar. Rows are kept a full slot apart
      // so nothing overlaps.
      for (let i = 0; i < 4; i++) slots.push({ index: 41 + (3 - i), col: 0, row: i });
      slots.push({ index: 45, col: 1.3, row: 3 }); // offhand
      slots.push({ index: 1, col: 5, row: 0.5 });
      slots.push({ index: 2, col: 6, row: 0.5 });
      slots.push({ index: 3, col: 5, row: 1.5 });
      slots.push({ index: 4, col: 6, row: 1.5 });
      slots.push({ index: 0, col: 8, row: 1 }); // crafting output
      for (let i = 0; i < 27; i++) slots.push({ index: 5 + 9 + i, col: i % 9, row: 4.4 + Math.floor(i / 9) });
      for (let i = 0; i < 9; i++) slots.push({ index: 5 + i, col: i, row: 7.8 });
      return { title: 'Inventory', width: 9, height: 9.2, slots };
    }
  }
}

export function prettyName(def: ItemDef): string {
  return def.name
    .replace(/_item$/, '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

function drawHeart(g: CanvasRenderingContext2D, x: number, y: number, size: number, fill: number): void {
  const draw = (color: string, clipRight: number) => {
    g.save();
    g.beginPath();
    g.rect(x, y, size * clipRight, size);
    g.clip();
    g.fillStyle = color;
    g.beginPath();
    const s = size / 16;
    g.moveTo(x + 8 * s, y + 14 * s);
    g.bezierCurveTo(x + 0 * s, y + 8 * s, x + 1 * s, y + 2 * s, x + 5 * s, y + 2 * s);
    g.bezierCurveTo(x + 7 * s, y + 2 * s, x + 8 * s, y + 4 * s, x + 8 * s, y + 5 * s);
    g.bezierCurveTo(x + 8 * s, y + 4 * s, x + 9 * s, y + 2 * s, x + 11 * s, y + 2 * s);
    g.bezierCurveTo(x + 15 * s, y + 2 * s, x + 16 * s, y + 8 * s, x + 8 * s, y + 14 * s);
    g.fill();
    g.restore();
  };
  draw('rgba(20,20,20,0.65)', 1);
  if (fill > 0) draw('#e33', fill);
}

function drawDrumstick(g: CanvasRenderingContext2D, x: number, y: number, size: number, fill: number): void {
  const draw = (color: string, clipRight: number) => {
    g.save();
    g.beginPath();
    g.rect(x, y, size * clipRight, size);
    g.clip();
    g.fillStyle = color;
    const s = size / 16;
    g.beginPath();
    g.arc(x + 10 * s, y + 6 * s, 4.5 * s, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.moveTo(x + 7 * s, y + 8 * s);
    g.lineTo(x + 2 * s, y + 13 * s);
    g.lineTo(x + 4 * s, y + 15 * s);
    g.lineTo(x + 9 * s, y + 10 * s);
    g.closePath();
    g.fill();
    g.restore();
  };
  draw('rgba(20,20,20,0.65)', 1);
  if (fill > 0) draw('#c98a3a', fill);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${url}`));
    img.src = url;
  });
}
